import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gmailAuthApi } from "@/services/gmailApi";
import { useToast } from "@/hooks/use-toast";

const gmailAuthKeys = {
  status: ["gmail", "auth", "status"] as const,
};

export const useGmailConnectionStatus = () => {
  return useQuery({
    queryKey: gmailAuthKeys.status,
    queryFn: () => gmailAuthApi.getStatus(),
    refetchInterval: false,
  });
};

export const useConnectGmail = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { authUrl } = await gmailAuthApi.initiateConnect();

      return new Promise<{ gmailEmail: string }>((resolve, reject) => {
        const popup = window.open(
          authUrl,
          "gmail-oauth",
          "width=500,height=600,scrollbars=yes,resizable=yes",
        );

        if (!popup) {
          reject(
            new Error("Popup was blocked. Please allow popups for this site."),
          );
          return;
        }

        let resolved = false;

        const cleanup = () => {
          clearInterval(pollInterval);
          clearInterval(checkPopupClosed);
          window.removeEventListener("message", handleMessage);
        };

        // Listen for postMessage from the OAuth callback page
        const handleMessage = (event: MessageEvent) => {
          if (event.data?.type === "gmail-auth-success") {
            if (resolved) return;
            resolved = true;
            cleanup();
            resolve({ gmailEmail: event.data.gmailEmail || "" });
          } else if (event.data?.type === "gmail-auth-error") {
            if (resolved) return;
            resolved = true;
            cleanup();
            reject(
              new Error(event.data.error || "Gmail authentication failed"),
            );
          }
        };

        window.addEventListener("message", handleMessage);

        const pollInterval = setInterval(async () => {
          if (resolved) return;
          try {
            const status = await gmailAuthApi.getStatus();
            if (status.connected) {
              resolved = true;
              cleanup();
              resolve({ gmailEmail: status.gmailEmail || "" });
            }
          } catch {
            // Ignore poll errors
          }
        }, 2000);

        // Check if popup was closed
        const checkPopupClosed = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(checkPopupClosed);
              window.removeEventListener("message", handleMessage);
              // Don't reject
            }
          } catch {
            clearInterval(checkPopupClosed);
          }
        }, 500);

        // Timeout after 5 minutes
        setTimeout(
          () => {
            if (!resolved) {
              resolved = true;
              cleanup();
              reject(
                new Error("Gmail connection timed out. Please try again."),
              );
            }
          },
          5 * 60 * 1000,
        );
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gmailAuthKeys.status });
      toast({
        title: "Gmail Connected",
        description: "Your Gmail account has been connected successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Gmail Connection Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDisconnectGmail = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: () => gmailAuthApi.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gmailAuthKeys.status });
      toast({
        title: "Gmail Disconnected",
        description: "Your Gmail account has been disconnected.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to disconnect Gmail. Please try again.",
        variant: "destructive",
      });
    },
  });
};
